import { useCallback, useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { 
  fetchProfessionalNPS, 
  IMonthlyAverage, 
  IProfessionalNPS, 
  IProfessionalNPSParams 
} from 'services/metrics/listProfessionalNPS'

export interface IMonthlyNpsAverage {
  month: string
  year: string
  average: number
  biannualAverage: number
  date: Date
}

interface IFormattedData {
  monthlyAverage: IMonthlyNpsAverage[]
}

interface UseGetProfessionalNPS {
  getProfessionalNPS: (params: IProfessionalNPSParams) => Promise<void>
  professionalNPS: IFormattedData
  loading: boolean
  error: boolean
}

function sortDataDescending(data: IMonthlyNpsAverage[]) {
  return data.sort((a, b) => {
    const dateA = new Date(a.year)
    const dateB = new Date(b.year)

    return dateB.getTime() - dateA.getTime()
  })
}

const formatDateFromSchedule = (data: IMonthlyAverage, biannualAverage: number) => {
  const splitDate = data.month.split('-')
  const year = splitDate[0]
  const monthNumber = splitDate[1]
  const formattedDate = new Date(Number(year), Number(monthNumber) - 1, 1)
  const formattedYear = format(formattedDate, 'yy')
  const formattedMonth = format(formattedDate, 'MMM', {
    locale: ptBR
  })

  const averageNps = data.average || 0

  const isAverageNpsNegative = averageNps < 0
  const isBiannualAverageNegative = biannualAverage < 0

  return {
    month: formattedMonth,
    year: formattedYear,
    average: isAverageNpsNegative ? 0 : averageNps,
    biannualAverage: isBiannualAverageNegative ? 0 : biannualAverage,
    date: formattedDate,
  }
}

function formatData(data: IProfessionalNPS) {
  const formattedMonthData = data.monthlyAverage.map((monthlyData) => 
    formatDateFromSchedule(monthlyData, data.biannualAverage)
  )

  return {
    monthlyAverage: sortDataDescending(formattedMonthData),
  }
}

function handleConcatMonthlyAverage(
  data: IMonthlyNpsAverage[], 
  newData: IMonthlyNpsAverage[]
) {
  return data.concat(newData)
}

export const useGetProfessionalNPS = (): UseGetProfessionalNPS => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [professionalNPS, setProfessionalNPS] = useState<IFormattedData>({
    monthlyAverage: []
  })

  const getProfessionalNPS = useCallback(async({
    startDate,
    endDate
  }: IProfessionalNPSParams) => {
    setLoading(true)
    try {
      const data = await fetchProfessionalNPS({startDate, endDate})
      const formattedData = formatData(data)

      setProfessionalNPS((prevState) => ({
        monthlyAverage: handleConcatMonthlyAverage(
          prevState.monthlyAverage,
          formattedData.monthlyAverage
        )
      }))

    } catch (error) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  return { 
    getProfessionalNPS,
    professionalNPS,
    loading,
    error
  }
}

